import { BlobArray } from "@pdfking/compatibility"
import type {
  Distance,
  PageNumber,
  SpatialRectangle,
} from "@pdfking/renderer-api"
import { PDFPlot } from "@pdfking/renderer-default"
import { Items, IterableToArray, Map } from "@pdfking/tfunc"

import { GenerateFile } from "../../files/GenerateFile"
import { RequestBlob } from "../../files/RequestBlob"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskPlotOptions } from "../../types/tasks/options/TaskPlotOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

// options PlotPdfOptions - file: Plot
// stream -
// emit TrackingCallback
//
// return DocumentPage[page, GeneratedFile[]] // takes the page number from PlotPdfOptions

export async function* TaskPlot(
  engine: PdfEngine,
  task: TaskPlotOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const emitter = (done: number) =>
    emit({
      name: TrackedTaskName.PLOT_PDF,
      done,
      total: 1,
    })

  void emitter(0)

  const content = await GenerateFile(
    engine,
    PDFPlot([task.page], {
      unit_to_px: task.options.unit_to_px,
      stroke_width: task.options.stroke_width,
      scale_bleed: task.options.scale_bleed,
      ...task.context,
      file_map: Object.fromEntries(
        Map(
          ([k, file]) => [
            k,
            RequestBlob(engine, file)
              .then(({ stream }) => IterableToArray(stream))
              .then(BlobArray),
          ],
          Items(task.context.file_map || {})
        )
      ),
    }),
    ContentType.PDF,
    emit
  )

  const bleed = task.page.bleed ?? task.context.page_bleed ?? (0 as Distance)
  const width = (task.page.width + bleed * 2) as Distance
  const height = (task.page.height + bleed * 2) as Distance

  const trim_box: SpatialRectangle<Distance> = {
    x: bleed,
    y: bleed,
    width: task.page.width,
    height: task.page.height,
  }

  const media_box: SpatialRectangle<Distance> = {
    x: 0 as Distance,
    y: 0 as Distance,
    width,
    height,
  }

  yield {
    enum: TaskName.CONTENT_FILE,
    name: "plot.pdf",
    content: {
      enum: VirtualFileType.SINGLE_PAGE_PDF,
      content,
      source: undefined,
      page: task.page,
      context: task.context,
      page_number: 1 as PageNumber,
      page_count: 1 as PageNumber,
      file_meta: {},
      size: media_box,
      trim_box,
      media_box,
    },
  }

  void emitter(1)
}
